import { api } from '@mmef/shared/api';
import type {
  EventOverview,
  EventQuery,
  EventSubmission,
  EventSubmissionResult,
  EventSummary,
} from '@mmef/shared/api';

/** One heading on the submission form's keyword picker, and the terms under it. */
export interface KeywordGroup {
  category: string;
  keywords: string[];
}

export const eventsApi = api.injectEndpoints({
  endpoints: (build) => ({
    listEvents: build.query<EventSummary[], EventQuery>({
      query: (params) => ({ url: '/events', params: pruned(params) }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Event' as const, id })), { type: 'Events' as const }]
          : [{ type: 'Events' as const }],
    }),

    /** Counts by city and the next few events, for the homepage before anyone searches. */
    getOverview: build.query<EventOverview, void>({
      query: () => '/events/overview',
      providesTags: [{ type: 'Events' }],
    }),

    getEvent: build.query<EventSummary, number>({
      query: (id) => `/events/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Event', id }],
    }),

    /**
     * The keyword vocabulary offered on the submission form.
     *
     * <p>Fetched rather than hard-coded here so there is one list. A copy in the front end would
     * drift from the extractor's, and the failure would be quiet: organisers picking terms that
     * no longer match anything, producing chips that filter to nothing.
     */
    listKeywords: build.query<KeywordGroup[], void>({
      query: () => '/keywords',
      // It changes when the code changes, which is to say never at runtime.
      keepUnusedDataFor: 3600,
    }),

    submitEvent: build.mutation<EventSubmissionResult, EventSubmission>({
      query: (body) => ({ url: '/events/submit', method: 'POST', body }),
      // Submissions land as PENDING_REVIEW, so the public list is unchanged — but the operator's
      // moderation queue just grew.
      invalidatesTags: ['AdminEvents'],
    }),
  }),
});

/**
 * Drops empty params so they never reach the query string. `?lat=` (blank) is not the same as an
 * absent `lat` to Spring's binder — it fails conversion instead of falling back to the default.
 */
function pruned(params: EventQuery): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  ) as Record<string, string | number>;
}

export const {
  useListEventsQuery,
  useGetOverviewQuery,
  useGetEventQuery,
  useListKeywordsQuery,
  useSubmitEventMutation,
} = eventsApi;
